import { generators } from '@playcastdotio/Messaging';
import { nonsoleReadinessReportSchema } from '@playcastdotio/NonsoleReadinessTypes';
import type { NonsoleReadinessReport } from '@playcastdotio/NonsoleReadinessTypes';
import type { IIpcService } from '../ipc/IIpcService';
import type { INonsoleReadinessService } from './INonsoleReadinessService';
import type { HostModeEntryResult } from './types';

const REPORT_TIMEOUT_MS = 10_000;
const ENTRY_TIMEOUT_MS = 70_000;

export interface ReplyMailbox<T> {
  addListener(listener: (state: T) => void): string;
  removeListener(id: string): void;
}

type NonsoleReadinessReply = {
  report?: unknown;
  error?: string;
  nonceEcho?: string;
} | null;

type HostModeEntryReply = {
  accepted: boolean;
  report?: unknown;
  error?: string;
  nonceEcho?: string;
} | null;

export interface NonsoleReadinessServiceDeps {
  ipcService: IIpcService;
  reportStore: ReplyMailbox<NonsoleReadinessReply>;
  entryStore: ReplyMailbox<HostModeEntryReply>;
}

type PendingKind = 'report' | 'entry';

interface PendingRequest {
  kind: PendingKind;
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
  timer: ReturnType<typeof setTimeout>;
}

export class NonsoleReadinessService implements INonsoleReadinessService {
  private readonly deps: NonsoleReadinessServiceDeps;
  private readonly pending = new Map<string, PendingRequest>();
  private readonly reportListenerId: string;
  private readonly entryListenerId: string;
  private destroyed = false;

  constructor(deps: NonsoleReadinessServiceDeps) {
    this.deps = deps;

    this.reportListenerId = deps.reportStore.addListener((state) => {
      this.handleReportReply(state);
    });

    this.entryListenerId = deps.entryStore.addListener((state) => {
      this.handleEntryReply(state);
    });
  }

  getFreshReport(): Promise<NonsoleReadinessReport> {
    if (this.destroyed) return Promise.reject(new Error('Service destroyed'));

    const nonce = crypto.randomUUID();

    return new Promise<NonsoleReadinessReport>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(nonce);
        reject(new Error('Timeout waiting for nonsole readiness report'));
      }, REPORT_TIMEOUT_MS);

      this.pending.set(nonce, { kind: 'report', resolve: resolve as (v: unknown) => void, reject, timer });
      this.deps.ipcService.send(generators.system.getNonsoleReadinessReport({ fresh: true, nonce }));
    });
  }

  requestEntry(): Promise<HostModeEntryResult> {
    if (this.destroyed) return Promise.reject(new Error('Service destroyed'));

    const nonce = crypto.randomUUID();

    return new Promise<HostModeEntryResult>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(nonce);
        reject(new Error('Timeout waiting for host mode entry result'));
      }, ENTRY_TIMEOUT_MS);

      this.pending.set(nonce, { kind: 'entry', resolve: resolve as (v: unknown) => void, reject, timer });
      this.deps.ipcService.send(generators.system.requestHostModeEntry(nonce));
    });
  }

  destroy(): void {
    this.destroyed = true;
    this.deps.reportStore.removeListener(this.reportListenerId);
    this.deps.entryStore.removeListener(this.entryListenerId);

    for (const [nonce, entry] of this.pending) {
      clearTimeout(entry.timer);
      entry.reject(new Error('Service destroyed'));
      this.pending.delete(nonce);
    }
  }

  private handleReportReply(state: NonsoleReadinessReply): void {
    if (!state || !state.nonceEcho) return;

    const entry = this.pending.get(state.nonceEcho);
    if (!entry || entry.kind !== 'report') return;

    clearTimeout(entry.timer);
    this.pending.delete(state.nonceEcho);

    if (state.error && !state.report) {
      entry.reject(new Error(`Report fetch failed: ${state.error}`));
      return;
    }

    const parsed = nonsoleReadinessReportSchema.safeParse(state.report);
    if (!parsed.success) {
      entry.reject(new Error(`Malformed readiness report: ${parsed.error.message}`));
      return;
    }

    entry.resolve(parsed.data);
  }

  private handleEntryReply(state: HostModeEntryReply): void {
    if (!state || !state.nonceEcho) return;

    const entry = this.pending.get(state.nonceEcho);
    if (!entry || entry.kind !== 'entry') return;

    clearTimeout(entry.timer);
    this.pending.delete(state.nonceEcho);

    if (state.accepted) {
      const parsed = nonsoleReadinessReportSchema.safeParse(state.report);
      if (!parsed.success) {
        entry.reject(new Error(`Malformed readiness report in accepted entry: ${parsed.error.message}`));
        return;
      }
      entry.resolve({ accepted: true, report: parsed.data } as HostModeEntryResult);
    } else {
      const result: HostModeEntryResult = { accepted: false };
      if (state.report) {
        const parsed = nonsoleReadinessReportSchema.safeParse(state.report);
        if (parsed.success) result.report = parsed.data;
      }
      if (state.error) result.error = state.error;
      entry.resolve(result);
    }
  }
}
